import React, { useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Inline, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import genericMessages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import { OptionTag } from '@atlassian/jira-option-color-picker/src/option-tag/index.tsx';
import {
	TagItem,
	TagItemPopupContentWrapper,
} from '@atlassian/jira-list-with-popup/src/ui/tag-item/index.tsx';
import ListWithPopup from '@atlassian/jira-list-with-popup/src/ui/main.tsx';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import type { ui_issueFieldMultiGroupPickerReadviewFull_MultiGroupPickerReadView$key } from './__generated__/ui_issueFieldMultiGroupPickerReadviewFull_MultiGroupPickerReadView.graphql';
import type { MultiGroupPickerReadViewProps } from './types.tsx';
import messages from './messages.tsx';

/**
 * The MultiGroupPickerReadView will show a read only view of MultiGroupPicker field.
 * @param props [MultiGroupPickerReadViewProps](./types.tsx)
 */
export const MultiGroupPickerReadView = ({
	fragmentRef,
	isTruncated,
	isTruncatedWithHoverPopover,
}: MultiGroupPickerReadViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const data = useFragment<ui_issueFieldMultiGroupPickerReadviewFull_MultiGroupPickerReadView$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_issueFieldMultiGroupPickerReadviewFull_MultiGroupPickerReadView on JiraMultipleGroupPickerField {
				selectedGroupsConnection {
					edges {
						node {
							id
							name
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const transformedSelectedOptions = useMemo(() => {
		if (!data?.selectedGroupsConnection?.edges) {
			return null;
		}

		const selectedOptions = data.selectedGroupsConnection.edges
			.map((edge) => edge?.node)
			.filter((node): node is { id: string; name: string } => !!node);

		return selectedOptions.map((node) => ({
			key: node.id,
			name: node.name,
			color: null,
			isInteractive: false,
		}));
	}, [data?.selectedGroupsConnection?.edges]);

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();
	if (!transformedSelectedOptions?.length) {
		return (
			<EmptyFieldText
				{...(isBespokeEmptyFieldTextGA && {
					placeholderText: genericMessages.bespokenMultiGroupPickerPlaceholderEmptyFieldText,
				})}
			/>
		);
	}

	if (isTruncated) {
		return (
			<Inline space="space.100" role="group" xcss={isTruncatedWithHoverPopover && inlineStyles}>
				<ListWithPopup
					items={transformedSelectedOptions}
					ItemComponent={isTruncatedWithHoverPopover ? TagItem : OptionTag}
					PopupContentWrapper={TagItemPopupContentWrapper}
					showMoreTooltip={formatMessage(messages.showMoreTooltip)}
					isHoverPopoverEnabled={isTruncatedWithHoverPopover}
				/>
			</Inline>
		);
	}

	return (
		<Inline space="space.100" role="group" shouldWrap>
			{transformedSelectedOptions.map(({ key, ...restProps }) => (
				<OptionTag key={key} {...restProps} />
			))}
		</Inline>
	);
};

const inlineStyles = xcss({
	width: '100%',
});
